import type { CSSProperties, FC } from "react";
import { useState } from "react";

import {
  AutoForm,
  AutoFormDataFieldDate,
  Button,
  DataFieldGroup,
  Modal,
  pickerCurrentTime,
  useAutoFormControls
} from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { ZonedDateTime } from "@internationalized/date";
import { IconCrossXCloseMissed } from "src/constants/icons";
import translate from "src/lib/translate";
import { validateDate } from "src/lib/zod/validateDate";
import DateTimePicker from "src/ui/components/DateTimePicker/DateTimePicker";
import Translate from "src/ui/components/Translate/Translate";
import { z } from "zod";

import { mealTakenAtAsZoned } from "./mealInsightHelpers";

const MealTakenAtSchema = z.object({
  date: validateDate
});

type FormValues = z.infer<typeof MealTakenAtSchema>;

const MODAL_STYLE = {
  "--max-width": "500px"
} as CSSProperties;

/* Header matches InDialog's (the waist / lab entry dialogs): centred title with
   the close control on its right, balanced by a spacer on the left. */
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  min-height: 3rem;
  background: var(--toolbar-background, var(--dialog-background));
`;

const Title = styled.h5`
  flex: 1 1 auto;
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
`;

const HeaderSpacer = styled.div`
  flex: 0 0 auto;
  width: 2.5rem;
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  background: transparent;
  border: 0;
  cursor: pointer;

  svg {
    width: 2rem;
    height: auto;
    color: var(--color-charcoal);
  }
`;

const Body = styled.div`
  padding: 0.5rem 1rem 2rem;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 1.5rem;
`;

interface MealTakenAtEditorProps {
  takenAt: string;
  onClose: () => void;
  onSave: (takenAt: ZonedDateTime) => void;
}

/**
 * Touch: the wheel sheet is the whole interaction, so tapping the meal's time
 * opens it directly rather than putting a form in the way.
 */
const TakenAtWheel: FC<MealTakenAtEditorProps> = ({
  takenAt,
  onClose,
  onSave
}) => (
  <DateTimePicker
    show
    initialDate={mealTakenAtAsZoned(takenAt)}
    granularity="minute"
    title={translate("mealInsights.takenAt.fieldLabel")}
    onFinish={(picked) => {
      onSave(picked);
      onClose();
    }}
  />
);

/**
 * Mouse and keyboard: the wheel is a poor fit, so the date/time is typed into
 * the segmented field the rest of the app's date forms use.
 */
const TakenAtForm: FC<MealTakenAtEditorProps> = ({
  takenAt,
  onClose,
  onSave
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const autoFormControls = useAutoFormControls({
    schema: MealTakenAtSchema,
    initialValue: { date: mealTakenAtAsZoned(takenAt) },
    validateOnChange: true,
    onSubmit: (data) => {
      onSave((data as unknown as FormValues).date);
      setIsOpen(false);
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      variant="alert"
      style={MODAL_STYLE}
      ariaLabel={translate("mealInsights.takenAt.dialogTitle")}
      onWillDismiss={() => setIsOpen(false)}
      onDidDismiss={onClose}
    >
      <Header>
        <HeaderSpacer />
        <Title>
          <Translate msg="mealInsights.takenAt.dialogTitle" />
        </Title>
        <CloseButton
          type="button"
          aria-label={translate("close")}
          onClick={() => setIsOpen(false)}
        >
          <IconCrossXCloseMissed />
        </CloseButton>
      </Header>

      <Body>
        <AutoForm {...autoFormControls.props}>
          <DataFieldGroup>
            <AutoFormDataFieldDate
              maxValue={pickerCurrentTime()}
              label={translate("mealInsights.takenAt.fieldLabel")}
              name="date"
              hourCycle={12}
              granularity="minute"
              hideTimeZone
            />
          </DataFieldGroup>

          <Actions>
            <Button type="submit" theme="charcoal">
              <Translate msg="mealInsights.save" />
            </Button>
          </Actions>
        </AutoForm>
      </Body>
    </Modal>
  );
};

/**
 * Edits when a meal was eaten. Which surface opens is decided once, on mount,
 * from the pointer type — the same coarse-pointer split the GLP-1 start date and
 * the self-reported lab forms make, but taken at the tap rather than on the
 * field's focus, since the meal header has no field to focus.
 */
const MealTakenAtEditor: FC<MealTakenAtEditorProps> = (props) => {
  const [isCoarsePointer] = useState(
    () => matchMedia("(pointer:coarse)").matches
  );

  return isCoarsePointer ? (
    <TakenAtWheel {...props} />
  ) : (
    <TakenAtForm {...props} />
  );
};

export default MealTakenAtEditor;
